/**
 * 공통 비동기 Hook
 * AsyncResult 타입을 사용한 비동기 상태 관리
 */
import { useState, useCallback } from 'react';
import { AsyncResult } from '../domain/types/AsyncResult';

/**
 * 비동기 작업을 관리하는 제네릭 Hook
 */
export const useAsync = <T, P extends unknown[] = []>(
    asyncFunction: (...params: P) => Promise<T>
) => {
    const [result, setResult] = useState<AsyncResult<T>>({ status: 'idle' });

    /**
     * 비동기 함수 실행
     */
    const execute = useCallback(
        async (...params: P) => {
            setResult({ status: 'loading' });
            try {
                const data = await asyncFunction(...params);
                setResult({ status: 'success', data });
                return data;
            } catch (error) {
                const err = error instanceof Error ? error : new Error(String(error));
                setResult({ status: 'error', error: err });
                throw err;
            }
        },
        [asyncFunction]
    );

    /**
     * 상태 초기화
     */
    const reset = useCallback(() => {
        setResult({ status: 'idle' });
    }, []);

    return {
        result,
        execute,
        reset,
        isLoading: result.status === 'loading',
        isSuccess: result.status === 'success',
        isError: result.status === 'error',
        isIdle: result.status === 'idle',
        error: result.status === 'error' ? result.error : null,
    };
};
